import { useState } from "react";
import type { ScheduledMessage } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Button, Dialog, DialogContent, DialogTitle } from "@ferrivia/ui";
import { Clock, CirclePause } from "lucide-react";
import { messageText } from "../ScheduleDialog/schedule";
import { scheduledMessageDomId } from "./focus";
import s from "./PendingScheduledMessage.module.css";

export interface PendingScheduledMessageProps {
  message: ScheduledMessage;
  clientName: string;
  staffUserId: string | undefined;
  onEdit: (message: ScheduledMessage) => void;
  onSendNow: (message: ScheduledMessage) => Promise<void>;
  onCancel: (message: ScheduledMessage) => Promise<void>;
}

export function PendingScheduledMessage({
  message,
  clientName,
  staffUserId,
  onEdit,
  onSendNow,
  onCancel,
}: PendingScheduledMessageProps) {
  const { t } = useTranslation("clinic");
  const { tag } = useFormatters();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const held = message.state === "held";
  const isAuthor = staffUserId !== undefined && message.authorId === staffUserId;
  const sendAt = new Date(message.sendAt);
  const when = new Intl.DateTimeFormat(tag, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(sendAt);

  let replyBy: string | null = null;
  if (message.ifNoReply !== "none" && message.replyByDays !== null) {
    const date = new Date(sendAt);
    date.setDate(date.getDate() + message.replyByDays);
    const formatted = new Intl.DateTimeFormat(tag, { weekday: "short", day: "numeric", month: "short" }).format(date);
    replyBy =
      message.ifNoReply === "nudge"
        ? t("customerWorkspace.scheduled.pending.noReplyNudge", {
            date: formatted,
            defaultValue: "No reply by {{date}} → send a nudge",
          })
        : t("customerWorkspace.scheduled.pending.noReplyRemind", {
            date: formatted,
            defaultValue: "No reply by {{date}} → remind me",
          });
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setFailed(false);
    try {
      await action();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  async function confirmSend() {
    setConfirming(false);
    await run(() => onSendNow(message));
  }

  const status = held
    ? t("customerWorkspace.scheduled.pending.heldMeta", "On hold")
    : t("customerWorkspace.scheduled.pending.scheduledMeta", { when, defaultValue: "Scheduled · {{when}}" });
  const meta = [message.authorName, replyBy].filter(Boolean);

  return (
    <article
      id={scheduledMessageDomId(message.id)}
      tabIndex={-1}
      className={held ? `${s.root} ${s.held}` : s.root}
      aria-label={t("customerWorkspace.scheduled.pending.regionLabel", "Scheduled message")}
    >
      {held ? (
        <p className={s.banner}>
          {t("customerWorkspace.scheduled.pending.heldBanner", {
            name: clientName,
            defaultValue: "{{name}} wrote after this was scheduled. It won't be sent until you reschedule or send it.",
          })}
        </p>
      ) : null}
      <p className={s.status}>
        {held ? <CirclePause aria-hidden size={14} /> : <Clock aria-hidden size={14} />}
        {status}
      </p>
      <div className={s.bubble}>{messageText(message)}</div>
      {meta.length > 0 ? <p className={s.meta}>{meta.join(" · ")}</p> : null}
      {failed ? (
        <p className={s.meta} role="alert">
          {t("customerWorkspace.scheduled.pending.actionFailed", "Couldn't do that. The message may already have been sent or cancelled.")}
        </p>
      ) : null}
      <div className={s.actions}>
        {isAuthor ? (
          <>
            <Button variant="secondary" className={s.action} disabled={busy} onClick={() => onEdit(message)}>
              {held
                ? t("customerWorkspace.scheduled.pending.reschedule", "Reschedule")
                : t("customerWorkspace.scheduled.pending.edit", "Edit")}
            </Button>
            <Button variant="secondary" className={s.action} disabled={busy} onClick={() => setConfirming(true)}>
              {t("customerWorkspace.scheduled.pending.sendNow", "Send now")}
            </Button>
          </>
        ) : null}
        <Button
          variant="secondary"
          className={`${s.action} ${s.cancel}`}
          disabled={busy}
          onClick={() => run(() => onCancel(message))}
        >
          {t("customerWorkspace.scheduled.pending.cancel", "Cancel")}
        </Button>
      </div>

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className={s.confirm} aria-describedby={undefined}>
          <DialogTitle className={s.confirmTitle}>
            {t("customerWorkspace.scheduled.pending.confirmTitle", "Send this message now?")}
          </DialogTitle>
          <p className={s.confirmBody}>{messageText(message)}</p>
          <div className={s.confirmActions}>
            <Button variant="secondary" onClick={() => setConfirming(false)}>
              {t("customerWorkspace.scheduled.pending.confirmDismiss", "Not now")}
            </Button>
            <Button onClick={confirmSend}>{t("customerWorkspace.scheduled.pending.confirmSend", "Send")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </article>
  );
}
